// Supply notes on catalogue products: how old a note is, and what the stock
// read says is on the shelf. Shared by every screen that shows a product's
// note (components/procurement/ProductSupplyNote.jsx); the API is
// src/api/routes/productNotes.js.
//
// A note is only as good as its age. Under 7 days it is fresh; from 7 to 30
// days it is ageing and read with care; over 30 days it is stale and likely
// out of date. A "valid until" date set by the PMO overrides the age: the day
// after it, the note has expired however recently it was logged. A stale or
// expired note is still shown, marked as such, because an old lead time is
// better than none as long as nobody mistakes it for a current one.

export const FRESH_UNDER_DAYS = 7;
export const STALE_OVER_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;
const localDay = (d) => { const x = new Date(d); return Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()); };
const isoDay = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; };

/**
 * How old a note is and what that means, as of `now`:
 * { days, state: 'fresh' | 'ageing' | 'stale' | 'expired', label }.
 */
export function noteAge(note, now = new Date()) {
  const days = Math.max(0, Math.round((localDay(now) - localDay(note.created_at)) / DAY_MS));
  const age = days === 0 ? 'Logged today' : `${days} day${days === 1 ? '' : 's'} old`;
  if (note.valid_until && note.valid_until < isoDay(now)) return { days, state: 'expired', label: `Expired, ${age.toLowerCase()}` };
  if (days > STALE_OVER_DAYS) return { days, state: 'stale', label: `${age}, likely out of date` };
  if (days >= FRESH_UNDER_DAYS) return { days, state: 'ageing', label: age };
  return { days, state: 'fresh', label: age };
}

export const isOutOfDate = (age) => age.state === 'stale' || age.state === 'expired';

const CONDITION_LABELS = { new: 'new', good: 'used (good)', fair: 'used (fair)', poor: 'used (poor)', damaged: 'damaged' };
export const conditionLabel = (c) => CONDITION_LABELS[c] || c;

const byLocation = (rows) => {
  const m = new Map();
  for (const r of rows) {
    const name = r.location_name || 'No location';
    m.set(name, (m.get(name) || 0) + r.qty);
  }
  return [...m.entries()].map(([name, qty]) => ({ name, qty })).sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name));
};

/**
 * The stock rows from GET /product-notes/:id, a row per condition and location,
 * as what the screens draw: new stock first with its locations, then each other
 * condition on its own line, so used or damaged units are never counted as new.
 */
export function summariseStock(rows = []) {
  const fresh = rows.filter(r => r.condition === 'new');
  const order = ['good', 'fair', 'poor', 'damaged'];
  const others = [...new Set(rows.filter(r => r.condition !== 'new').map(r => r.condition))]
    .sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99))
    .map(condition => {
      const these = rows.filter(r => r.condition === condition);
      return { condition, label: conditionLabel(condition), qty: these.reduce((s, r) => s + r.qty, 0), locations: byLocation(these) };
    });
  return {
    newQty: fresh.reduce((s, r) => s + r.qty, 0),
    newLocations: byLocation(fresh),
    others,
  };
}

export const describeLocations = (locations) => locations.map(l => `${l.qty} at ${l.name}`).join(', ');
